import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowDown, Feather, Layers, Sun } from "lucide-react";
import { api } from "../lib/api";
import { RevealLines, Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { ProtectedImage } from "../components/ProtectedImage";

const HERO_IMG =
  `${process.env.REACT_APP_BACKEND_URL}/api/static/hero_bg.png`;
const STUDIO_IMG =
  "https://images.unsplash.com/photo-1742440710226-450e3b85c100?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxhcmNoaXRlY3R1cmUlMjBzdHVkaW8lMjBvZmZpY2UlMjBkZXNpZ25lcnMlMjB3b3JraW5nfGVufDB8fHx8MTc4NDk2MzU1M3ww&ixlib=rb-4.1.0&q=85";

const APPROACH = [
  {
    title: "Curating experiences",
    body: "Beyond floor plans, we choreograph moments — the arrival, the morning light in the kitchen, the retreat at dusk. Each room is a scene in a longer story of how you'll live.",
    Icon: Feather,
  },
  {
    title: "Alternative construction techniques",
    body: "Rammed earth, compressed stabilised earth blocks, exposed lime and reclaimed timber. We work with material systems that lower embodied carbon and celebrate the hand that built them.",
    Icon: Layers,
  },
  {
    title: "Thermal comfort",
    body: "Passive cooling first — orientation, shading, cavity walls, cross-ventilation and courtyards. A BlueGreen home stays cool through Bangalore's afternoons without leaning on the grid.",
    Icon: Sun,
  },
];

const ApproachSection = () => (
  <section id="approach" className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-32" data-testid="approach-section">
    <Reveal className="grid md:grid-cols-12 gap-10 items-end mb-16 md:mb-24">
      <div className="md:col-span-7">
        <p className="text-xs uppercase tracking-[0.3em] font-bold text-teal mb-6">Our Approach</p>
        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[0.9] tracking-tight text-ink">
          A quiet, considered<br /><span className="italic text-stone">way of building.</span>
        </h2>
      </div>
      <p className="md:col-span-5 text-stone text-base leading-relaxed">
        Three convictions shape every project that leaves our studio — from the smallest interior to the largest residence.
      </p>
    </Reveal>

    <div className="grid md:grid-cols-3 gap-10 md:gap-14">
      {APPROACH.map((a, i) => (
        <Reveal key={a.title} delay={i * 0.12}>
          <div className="group relative pt-10" data-testid={`approach-item-${i}`}>
            {/* animated top rule */}
            <span className="absolute top-0 left-0 h-px w-16 bg-forest transition-all duration-700 ease-[cubic-bezier(0.76,0,0.24,1)] group-hover:w-full group-hover:bg-teal" />

            <div className="flex items-center gap-5 mb-10">
              <a.Icon size={30} strokeWidth={1.25} className="text-forest group-hover:text-teal transition-colors duration-500" />
              <div className="flex-1 h-px bg-ink/10" />
            </div>

            <h3 className="font-serif text-3xl md:text-[1.8rem] tracking-tight text-ink leading-[1.05] mb-6 group-hover:text-teal transition-colors duration-500">
              {a.title}
            </h3>
            <p className="text-stone text-sm md:text-base leading-relaxed max-w-md">
              {a.body}
            </p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const FAQ_ITEMS = [
  {
    q: "What type of architecture does Blue Green Studio design?",
    a: "We are a research-driven, sustainable architecture practice based in Bangalore. Our work spans climate-responsive residences, tropical-modern homes, courtyard houses and interior fit-outs — all rooted in the Three C's: Climate, Context and Culture.",
  },
  {
    q: "Where is Blue Green Studio located and where do you take on projects?",
    a: "Our studio is in Jayanagar, Bengaluru. We work across Bangalore (Whitefield, Sarjapur, Koramangala, JP Nagar, Devanahalli), across Karnataka and take on select projects across South India.",
  },
  {
    q: "Do you design eco-friendly and sustainable homes?",
    a: "Yes — sustainability is a starting point, not an add-on. We prioritise passive cooling, cross-ventilation, low-embodied-carbon materials such as compressed stabilised earth blocks and lime plaster, native landscape, and wellness-focused interiors.",
  },
  {
    q: "Do you work with alternative construction techniques like rammed earth?",
    a: "We do. Rammed earth, CSEB masonry, exposed lime plaster and reclaimed timber joinery feature regularly in our residences. We match the technique to the site, brief and budget — never as a stylistic imposition.",
  },
  {
    q: "How do I start a project with Blue Green Studio?",
    a: "The easiest way is our two-minute Design Quiz — share your style, size, budget and timeline and we will respond with a considered point of view within two working days.",
  },
];

const FaqSection = () => (
  <section id="faq" className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-32" data-testid="faq-section">
    <Reveal className="mb-14 md:mb-20">
      <p className="text-xs uppercase tracking-[0.3em] font-bold text-teal mb-6">Frequently Asked</p>
      <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[0.9] tracking-tight text-ink">
        Questions we hear <br /><span className="italic text-stone">most often.</span>
      </h2>
    </Reveal>
    <div className="border-t border-ink/10">
      {FAQ_ITEMS.map((item, i) => (
        <Reveal key={item.q} delay={i * 0.05}>
          <details className="group border-b border-ink/10 py-8 md:py-10" data-testid={`faq-item-${i}`}>
            <summary className="cursor-pointer list-none flex items-start justify-between gap-8">
              <h3 className="font-serif text-2xl md:text-3xl tracking-tight text-ink group-hover:text-teal transition-colors leading-snug flex-1 text-left">
                {item.q}
              </h3>
              <span className="text-teal text-3xl leading-none mt-1 transition-transform duration-500 group-open:rotate-45">+</span>
            </summary>
            <p className="mt-6 max-w-3xl text-stone text-base leading-relaxed text-left">{item.a}</p>
          </details>
        </Reveal>
      ))}
    </div>
  </section>
);

const JournalSection = ({ posts }) => {
  if (!posts.length) return null;
  const [first, ...rest] = posts;
  const meta = (p) =>
    `${new Date(p.published_date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })} · ${p.read_time}`;
  return (
    <section id="journal" className="max-w-[1600px] mx-auto px-6 md:px-12 py-20 md:py-28" data-testid="journal-section">
      <Reveal className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-10 md:mb-14">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] font-bold text-teal mb-4">From the Journal</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[0.9] tracking-tight text-ink">
            Writing on climate,<br /><span className="italic text-stone">cities and craft.</span>
          </h2>
        </div>
        <Link
          to="/insights"
          data-testid="journal-view-all"
          className="link-underline text-xs uppercase tracking-[0.2em] font-bold text-forest hover:text-teal transition-colors"
        >
          All Insights
        </Link>
      </Reveal>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
        <Reveal className="col-span-2 md:row-span-2">
          <Link
            to={`/insights/${first.slug}`}
            data-testid={`journal-item-${first.slug}`}
            className="group block h-full"
          >
            <div className="clip-frame relative h-full aspect-[16/10] md:aspect-auto bg-sand-dark overflow-hidden">
              <img
                src={first.cover_image}
                alt={first.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/30 to-ink/10" />
              <span className="absolute top-5 left-5 bg-sand/90 backdrop-blur px-4 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-ink">
                {first.category}
              </span>
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-left">
                <h3 className="font-serif text-2xl md:text-4xl tracking-tight text-sand leading-tight group-hover:text-teal transition-colors duration-500">
                  {first.title}
                </h3>
                <p className="mt-3 text-sand/75 text-sm leading-relaxed max-w-md line-clamp-2">{first.excerpt}</p>
                <p className="mt-4 text-[10px] uppercase tracking-[0.2em] text-sand/60">{meta(first)}</p>
              </div>
            </div>
          </Link>
        </Reveal>

        {rest.map((p, i) => (
          <Reveal key={p.slug} delay={(i % 2) * 0.08}>
            <Link
              to={`/insights/${p.slug}`}
              data-testid={`journal-item-${p.slug}`}
              className="group block"
            >
              <div className="clip-frame relative aspect-square bg-sand-dark overflow-hidden">
                <img
                  src={p.cover_image}
                  alt={p.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/40 to-ink/5" />
                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5 text-left">
                  <h3 className="font-serif text-sm md:text-xl tracking-tight text-sand leading-snug line-clamp-3 group-hover:text-teal transition-colors duration-500">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-[10px] uppercase tracking-[0.2em] text-sand/70">{p.read_time}</p>
                </div>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const MANIFESTO = [
  { n: "01", title: "Climate", body: "We design for Bangalore's monsoons, mid-day glare and cool nights. Deep verandahs, cross-ventilation, shaded courtyards and passive cooling are the starting point — not the afterthought." },
  { n: "02", title: "Context", body: "Every home begins with its ground — the slope, the mature trees, the neighbourhood grain. Architecture that belongs to its plot, its street and its city, rather than being imported from elsewhere." },
  { n: "03", title: "Culture", body: "How you cook, celebrate, host, retreat. Our residences are shaped by the rituals of the families who live in them — inclusive, generous and unmistakably yours." },
  { n: "04", title: "Research-driven & Sustainable", body: "Every material, section and system is tested against a simple question: will this be kinder to its inhabitants and its planet in twenty years? Eco-friendly by default, wellness-focused by design." },
];

const HeroSection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section ref={ref} className="relative min-h-screen flex flex-col justify-end overflow-hidden" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y, scale }}>
        <motion.img
          src={HERO_IMG}
          alt="Blue Green Studio residence"
          className="w-full h-[115%] object-cover"
          initial={{ scale: 1.2 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.8, ease: [0.16, 1, 0.3, 1] }}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-ink/40 via-ink/10 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/10 via-transparent to-ink/25" />
      </motion.div>

      <div className="relative max-w-[1600px] mx-auto w-full px-6 md:px-12 pb-16 md:pb-24 pt-32 md:pt-40">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 1 }}
          className="text-xs uppercase tracking-[0.3em] font-bold text-ink/80 mb-10 md:mb-14"
        >
          Sustainable &nbsp;/&nbsp; Research-driven &nbsp;/&nbsp; Bangalore
        </motion.p>
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 1 }}
          className="inline-block bg-teal/55 backdrop-blur-sm text-sand font-serif italic font-medium text-2xl md:text-[3vw] leading-[1] px-6 md:px-8 py-4 md:py-5 mb-4 md:mb-6"
        >
          Architecture that responds to —
        </motion.p>
        <RevealLines
          as="h1"
          delay={0.35}
          className="font-serif text-ink text-[9.75vw] md:text-[6vw] leading-[0.9] tracking-tighter"
          lines={["Climate.", "Context.", <span key="c" className="italic text-teal">Culture.</span>]}
        />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="mt-10 flex flex-col md:flex-row md:items-end gap-8"
        >
          <a href="#work" className="flex items-center gap-3 text-ink/80 text-xs uppercase tracking-[0.2em] font-bold group order-2 md:order-1">
            <span className="w-12 h-12 rounded-full border border-ink/40 flex items-center justify-center group-hover:bg-forest group-hover:text-sand group-hover:border-forest transition-colors duration-500">
              <ArrowDown size={18} />
            </span>
            Explore Work
          </a>
          <p className="max-w-md bg-teal/55 backdrop-blur-sm text-sand px-6 py-5 text-sm md:text-base leading-relaxed order-1 md:order-2 md:mr-auto">
            We design with climate, context and culture in mind — <span className="text-sand font-semibold underline decoration-sand/40 underline-offset-4">eco-friendly</span>, <span className="text-sand font-semibold underline decoration-sand/40 underline-offset-4">research-driven</span> architecture that turns residences into <span className="text-sand font-semibold underline decoration-sand/40 underline-offset-4">sustainable</span>, inclusive, wellness-focused experiences.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

const MarqueeBand = () => (
  <section className="bg-sand-dark py-8 border-y border-ink/10" data-testid="marquee-band">
    <Marquee speed={30} gradient={false}>
      {["Sustainable", "Eco-friendly", "Research-driven", "Inclusive", "Wellness-focused", "Climate · Context · Culture"].map((w, i) => (
        <span key={i} className="font-serif italic text-3xl md:text-5xl text-forest mx-8 flex items-center gap-16">
          {w}
          <span className="text-teal not-italic text-2xl">✳</span>
        </span>
      ))}
    </Marquee>
  </section>
);

const WorkGrid = ({ projects }) => (
  <section id="work" className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-32" data-testid="work-section">
    <Reveal className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-16 md:mb-24">
      <div>
        <p className="text-xs uppercase tracking-[0.3em] font-bold text-clay mb-4">Selected Work</p>
        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[0.9] tracking-tight text-ink">
          A portfolio of<br /><span className="italic text-stone">quiet homes</span>
        </h2>
      </div>
      <p className="max-w-xs text-stone text-sm">Each project is a dialogue between client, climate and craft. Explore a selection of our built residences.</p>
    </Reveal>

    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
      {projects.map((p, i) => (
        <Reveal key={p.id} delay={(i % 3) * 0.08}>
          <Link to={`/projects/${p.id}`} data-testid={`project-card-${p.id}`} className="group block">
            <div className="clip-frame aspect-[4/5] bg-sand-dark relative">
              <ProtectedImage src={p.cover_image} alt={p.title} imgClassName="w-full h-full object-cover" className="w-full h-full" />
              <div className="absolute inset-0 bg-ink/10 group-hover:bg-ink/0 transition-colors duration-500 z-[3]" />
              <span className="absolute top-5 left-5 bg-sand/80 backdrop-blur px-4 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-ink z-[4]">
                {p.category}
              </span>
            </div>
            <div className="flex items-baseline justify-between mt-5 gap-4">
              <h3 className="font-serif text-2xl md:text-3xl tracking-tight text-ink group-hover:text-clay transition-colors flex items-center gap-2">
                {p.title}
                <ArrowUpRight className="opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-500 text-clay" size={22} />
              </h3>
              <span className="text-xs uppercase tracking-[0.2em] text-stone whitespace-nowrap">{p.location} · {p.year}</span>
            </div>
          </Link>
        </Reveal>
      ))}
    </div>
  </section>
);

const Manifesto = () => (
  <section id="studio" className="bg-ink text-sand py-24 md:py-36" data-testid="manifesto-section">
    <div className="max-w-[1600px] mx-auto px-6 md:px-12">
      <div className="grid md:grid-cols-12 gap-12 mb-20 md:mb-32 items-end">
        <div className="md:col-span-6">
          <p className="text-xs uppercase tracking-[0.3em] font-bold text-teal mb-6">The Three C's</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[0.9] tracking-tight">
            Climate. Context.<br /><span className="italic text-stone">Culture.</span>
          </h2>
          <p className="mt-8 text-sand/70 text-base leading-relaxed max-w-md">
            A research-driven practice designing sustainable, inclusive and wellness-focused homes across Bangalore.
          </p>
        </div>
        <div className="md:col-span-6 clip-frame aspect-[16/10]">
          <img src={STUDIO_IMG} alt="Blue Green Studio team" className="w-full h-full object-cover" />
        </div>
      </div>

      <div className="border-t border-sand/10">
        {MANIFESTO.map((m, i) => (
          <Reveal key={m.n} delay={i * 0.05}>
            <div className="relative grid md:grid-cols-12 gap-6 py-14 md:py-16 border-b border-sand/10 group">
              <span className="pointer-events-none absolute -top-6 right-0 font-serif text-[7.5rem] md:text-[10.5rem] leading-none text-sand/5 select-none">
                {m.n}
              </span>
              <div className="md:col-span-4 relative z-10">
                <h3 className="font-serif text-2xl md:text-4xl tracking-tight group-hover:text-clay transition-colors">{m.title}</h3>
              </div>
              <p className="md:col-span-6 md:col-start-6 relative z-10 text-sand/70 text-base leading-relaxed">{m.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const QuizCTA = () => (
  <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-32" data-testid="quiz-cta-section">
    <Reveal className="bg-sand-dark border border-ink/10 px-8 md:px-20 py-16 md:py-24 text-center">
      <p className="text-xs uppercase tracking-[0.3em] font-bold text-clay mb-6">Not sure where to start?</p>
      <h2 className="font-serif text-3xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-ink max-w-4xl mx-auto">
        Tell us the home you dream of.
      </h2>
      <p className="mt-8 text-stone max-w-xl mx-auto text-base">
        Take our two-minute design quiz. Share your style, scale and budget — we'll respond with a considered point of view.
      </p>
      <Link
        to="/quiz"
        data-testid="quiz-cta-button"
        className="inline-block mt-10 bg-forest text-sand px-10 py-5 text-xs uppercase tracking-[0.2em] font-bold hover:bg-clay transition-colors duration-500"
      >
        Take the Quiz
      </Link>
    </Reveal>
  </section>
);

export default function Home() {
  const [projects, setProjects] = useState([]);
  const [journalPosts, setJournalPosts] = useState([]);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    api
      .get("/insights")
      .then((r) => setJournalPosts((r.data || []).filter((p) => p.show_on_home)))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="home-page" className="text-right">
      <Seo
        title="Sustainable Architecture Firm in Bangalore"
        description="Blue Green Studio designs climate-responsive, eco-friendly, wellness-focused homes across Bangalore. Explore our residential portfolio or take the two-minute home design quiz."
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebSite",
              name: "Blue Green Studio",
              url: typeof window !== "undefined" ? window.location.origin : "",
              potentialAction: {
                "@type": "SearchAction",
                target: {
                  "@type": "EntryPoint",
                  urlTemplate: `${typeof window !== "undefined" ? window.location.origin : ""}/?q={search_term_string}`,
                },
                "query-input": "required name=search_term_string",
              },
            },
            {
              "@type": "FAQPage",
              mainEntity: FAQ_ITEMS.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
          ],
        }}
      />
      <HeroSection />
      <MarqueeBand />
      <JournalSection posts={journalPosts} />
      <ApproachSection />
      <WorkGrid projects={projects} />
      <Manifesto />
      <FaqSection />
      <QuizCTA />
    </div>
  );
}
